import { useSearchParams } from 'react-router';
import {
	Calendar,
	Clock,
	ImageOff,
	MapPin,
	User
} from 'lucide-react';
import { getHighestOrderImageUrl } from 'utils';
import { useGetUserCampaignInvitations, useGetUserCampaignReward } from 'api/invite/hooks';
import Styles from './success.module.scss';

export const InvitationSuccess = () => {
	const [searchParams] = useSearchParams();
	const restaurantID = searchParams.get('restaurantID');
	const campaignID = searchParams.get('campaignID');
	const userID = localStorage.getItem('invitee_user_id');

	const {
		data: userCampaignReward,
	} = useGetUserCampaignReward({
		restaurantID,
		campaignID,
		userID
	});

	const {
		data: userCampaignInvitations,
	} = useGetUserCampaignInvitations({
		restaurantID,
		campaignID,
		userID
	});

	const userReward = userCampaignReward?.user_campaign_reward?.campaign_reward || null;

	const campaignInvites = (userCampaignInvitations?.campaign_invites
		&& userCampaignInvitations?.campaign_invites.length > 0)
		? userCampaignInvitations?.campaign_invites
		: [];

	const invitedGuestName = `${searchParams.get('firstName') || '-'} ${searchParams.get('lastName') || '-'}`;

	const getReservedMaskImage = (invite) => {
		const images = invite.user_campaign_rewards?.[0]?.campaign_reward?.campaign_reward_images;

		return images?.length
			? getHighestOrderImageUrl(images)
			: null;
	};

	return (
		<div className={Styles.container}>
			<div className={Styles.detailCardContainer}>
				<h2 className='text-capitalize'>We look forward to unlocking our secrets for you, {invitedGuestName}</h2>
				<div className={Styles.detailCard}>
					<div className='py-3 px-3 w-100'>
						<div className='d-flex align-items-center justify-content-between'>
							<h4 className='mb-1'>Masquerade Ball Rsvp</h4>
							<a
								href='/profile'
								className={Styles.detailsButton}>
								View Details
							</a>
						</div>
						<div className='mt-1 d-flex gap-3 flex-wrap gap-md-4'>
							<p className='mb-0 d-flex align-items-center gap-2'>
								<span><Calendar className='mb-1' size={18} /></span>
								Sat Jan 31, 2026
							</p>
							<p className='mb-0 d-flex align-items-center gap-2'>
								<span><Clock className='mb-1' size={18} /></span>
								7:30 PM
							</p>
							<p className='mb-0 d-flex align-items-center gap-2'>
								<span><User className='mb-1' size={18} /></span>
								{campaignInvites ? campaignInvites.length : '-'} Guests
							</p>
							<p className='mb-0 d-flex align-items-center gap-2'>
								<span><MapPin className='mb-1' size={18} /></span>
								Speakeasy Restaurant, Emaar, Karachi
							</p>
						</div>
					</div>
					<div className={`${Styles.detailSection} py-3 px-3`}>
						<div className={Styles.guestList}>
							{userReward
								&& (
									<div className='d-flex align-items-center gap-4'>
										{
											(userReward.campaign_reward_images && userReward.campaign_reward_images) ? (
												<img
													className={Styles.maskImage}
													src={getHighestOrderImageUrl(userReward.campaign_reward_images)}
													alt={`${invitedGuestName}'s reserved mask`}
												/>
											) : (
												<div className={Styles.emptyImage}>
													<ImageOff color='#000' size={16} />
												</div>
											)
										}
										<div>
											<h5 className='mb-0'>{invitedGuestName}'s Reserved Mask</h5>
											<h6>Invited Guest</h6>
										</div>
									</div>
								)
							}
							{campaignInvites.length > 0
								&& campaignInvites.map((invite) => {
									const imageUrl = getReservedMaskImage(invite);

									return (
										<div key={invite.id} className="d-flex align-items-center gap-4">
											{imageUrl ? (
												<img
													className={Styles.maskImage}
													src={imageUrl}
													alt={`${invite.invitee.first_name}'s reserved mask`}
												/>
											) : (
												<div className={Styles.emptyImage}>
													<ImageOff color="#000" size={16} />
												</div>
											)}

											<div>
												<h5 className="mb-0 text-capitalize">
													{`${invite.invitee.first_name} ${invite.invitee.last_name}'s Reserved Mask`}
												</h5>
												<h6>{invitedGuestName}'s Guest</h6>
											</div>
										</div>
									);
								})}
						</div>
						<div className={Styles.importantDetails}>
							<h4 className='text-center text-md-start'>Important Details</h4>
							<p className='mb-2'>
								Reserved masks will be available when checking in for the event
							</p>
							<p className='mb-2'>
								Only guests who have been RSVP-ed will be permitted entry
							</p>
							<p className='mb-2'>
								Please comply with all Emaar security team requests. When
								prompted for entry information at the gate, provide the name for
								the INVITED GUEST ({invitedGuestName}) against Pearl Tower 2 Retail 6
							</p>
							<p className='mb-2'>
								Dress code will be strictly enforced. If you and your guest are not in
								dress code, you will be barred from entry to the event
							</p>
							<p>
								Small bites and an assortment of beverages will be served at the
								event. No alcohol nor smoking of any kind is permitted at the event
							</p>
						</div>
					</div>
				</div>
			</div>
			<div className={Styles.dressCodeCardContainer}>
				<h2 className='text-center'>Dress Code</h2>
				<p className='text-center'>
					Masquerade balls have a simple dress code; classy.
				</p>
				<p className='text-center'>
					If in doubt about what is considered classy, watch the Great Gatsby before coming.
				</p>
				<h6>Females</h6>
				<p>
					Attendees identifying as the fairer-sex are encouraged to wear evening
					dresses. We want our classy queens to sparkle in all the right ways so
					keep things modest and elevated.
				</p>
				<p>
					If wearing cultural clothing. stick to solid colors/party-wear.
				</p>
				<h6>Males</h6>
				<p>
					A man can wear nothing finer than a dapper tuxedo. We encourage you to
					think "black tie" but if that's not your vibe then blazers will suffice. Just
					don't blame us if you look under-dressed!
				</p>
				<p>
					If wearing cultural clothing, stick to solid colors and consider elevating
					with a waistcoat/suit jacket.
				</p>
			</div>
		</div>
	);
};
